import { AxiosResponse } from 'axios';
import { ALL_PERMISSIONS } from '../index';
import Archivable, { ArchivedUpdatedEvent } from './shared/archivable';
import Entity from './shared/entity';
import { Event } from './shared/eventstore-client/EventstoreClient';
import { Fields } from './shared/fields';
import { ServiceClientWithEventstore } from './shared/service-client/ServiceClientWithEventstore';

export interface MessageResponse {
  message: string;
}

export interface CustodianProperties {
  name: string;
  location: string;
  info?: Fields | null;
  permissions?: ALL_PERMISSIONS;
}

export interface GetCustodiansResponse {
  custodians: Custodian[];
  continuationToken?: string;
}

export interface GetCustodiansRequest {
  limit?: number;
  continuationToken?: string;
  allowArchived?: boolean;
}

export interface Custodian extends Entity, CustodianProperties, Archivable {}

export type CreateCustodian = CustodianProperties;
export type UpdateCustodian = Partial<CustodianProperties>;

export type PERMISSIONS =
  | typeof DELETE
  | typeof CREATE
  | typeof UPDATE
  | typeof UPDATE_ANY
  | typeof UPDATE_PERMISSIONS
  | typeof GET
  | typeof GET_ALL
  | typeof SET_ARCHIVED
  | typeof GET_ALL_PERMISSIONS
  | typeof GET_PERMISSIONS
  | typeof GET_EVENTS_ANY
  | typeof GET_EVENTS_CREATED
  | typeof GET_EVENTS_DELETED
  | typeof GET_EVENTS_UPDATED;

export const DELETE = 'CUSTODIAN:DELETE';
export const CREATE = 'CUSTODIAN:CREATE';
export const GET = 'CUSTODIAN:GET';
export const GET_ALL = 'CUSTODIAN:GET_ALL';
export const SET_ARCHIVED = 'CUSTODIAN:SET_ARCHIVED';
export const GET_ALL_PERMISSIONS = 'CUSTODIAN:GET_ALL:PERMISSIONS';
export const GET_PERMISSIONS = 'CUSTODIAN:GET:PERMISSIONS';

/* Gives permission to update custodians that you belong to */
export const UPDATE = 'CUSTODIAN:UPDATE';
/* Gives permission to update any custodian, even if you dont belong to it */
export const UPDATE_ANY = 'CUSTODIAN:UPDATE:ANY';
/* Required to update permissions of a custodian */
export const UPDATE_PERMISSIONS = 'CUSTODIAN:UPDATE:PERMISSIONS';

export const GET_EVENTS_ANY = 'CUSTODIAN:GET_EVENTS:ANY';
export const GET_EVENTS_CREATED = 'CUSTODIAN:GET_EVENTS:CREATED';
export const GET_EVENTS_DELETED = 'CUSTODIAN:GET_EVENTS:DELETED';
export const GET_EVENTS_UPDATED = 'CUSTODIAN:GET_EVENTS:UPDATED';
export const GET_EVENTS_ARCHIVED_UPDATED = 'CUSTODIAN:GET_EVENTS:ARCHIVED_UPDATED';

export const PERMISSION_NAMES = {
  CREATE,
  DELETE,
  UPDATE,
  UPDATE_ANY,
  UPDATE_PERMISSIONS,
  GET,
  GET_ALL,
  SET_ARCHIVED,
  GET_ALL_PERMISSIONS,
  GET_PERMISSIONS,
  GET_EVENTS_ANY,
  GET_EVENTS_CREATED,
  GET_EVENTS_DELETED,
  GET_EVENTS_UPDATED,
};

export interface CreatedEvent extends Event {
  type: 'CREATED';
  data: Custodian;
}

export interface UpdatedEvent extends Event {
  type: 'UPDATED';
  data: UpdateCustodian;
}

export interface DeletedEvent extends Event {
  type: 'DELETED';
}

export type CustodianEvents = CreatedEvent | DeletedEvent | UpdatedEvent | ArchivedUpdatedEvent;

export class CustodianClient extends ServiceClientWithEventstore<CustodianEvents> {
  public async getAll(input: GetCustodiansRequest = {}): Promise<AxiosResponse<GetCustodiansResponse>> {
    return await this.httpClient.get('/', {
      params: input,
      ...this.requestConfig,
    });
  }

  public async get(custodianId: string): Promise<AxiosResponse<Custodian>> {
    return await this.httpClient.get(`/${custodianId}`, this.requestConfig);
  }

  public async create(createCustodian: CreateCustodian): Promise<AxiosResponse<Custodian>> {
    return await this.httpClient.post('/', createCustodian, this.requestConfig);
  }

  public async delete(custodianId: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(`/${custodianId}`, this.requestConfig);
  }

  public async update(custodianId: string, c: UpdateCustodian): Promise<AxiosResponse<Custodian>> {
    return await this.httpClient.patch(`/${custodianId}`, c, this.requestConfig);
  }

  public async setArchived(id: string, archived: boolean): Promise<AxiosResponse<Custodian>> {
    return await this.httpClient.put(`/${id}/archived`, { archived }, this.requestConfig);
  }
}
